import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";

type AutoSaveStatus = "idle" | "saving" | "saved";

const DEBOUNCE_DELAY = 1000;
const SAVED_DISPLAY_DURATION = 2000;

export default function useAutoSave(
  ticketId: string,
  serverValue: string,
  onSave: (value: string) => void,
  transform?: (value: string) => string,
) {
  const [draft, setDraft] = useState(serverValue);
  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    setDraft(serverValue);
  }, [ticketId, serverValue]);

  const debouncedSaveRef = useRef(
    debounce((value: string, currentServerValue: string) => {
      if (value !== currentServerValue) {
        setStatus("saving");
        try {
          onSaveRef.current(value);
        } finally {
          setStatus("saved");
          savedTimerRef.current = setTimeout(
            () => setStatus("idle"),
            SAVED_DISPLAY_DURATION,
          );
        }
      }
    }, DEBOUNCE_DELAY),
  );

  useEffect(() => {
    return () => {
      debouncedSaveRef.current.cancel();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      const transformed = transform ? transform(value) : value;
      setDraft(transformed);
      setStatus("idle");
      debouncedSaveRef.current(transformed, serverValue);
    },
    [serverValue, transform],
  );

  const flush = useCallback(() => {
    debouncedSaveRef.current.flush();
  }, []);

  return { draft, setDraft: handleChange, status, flush };
}
